import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/App';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Invalid email or password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="border-b border-ink-200 bg-paper/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo-link">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <Link to="/" className="text-sm text-ink-600 hover:text-ink flex items-center gap-1.5" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 grid-bg">
        <div className="w-full max-w-md">
          <div className="card-base p-8">
            <p className="eyebrow mb-3">SIGN IN</p>
            <h1 className="h2 mb-1">Welcome back.</h1>
            <p className="text-ink-600 text-sm mb-6">Sign in to check what's coming up.</p>

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="btn btn-outline w-full mb-4"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-ink-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 eyebrow">OR EMAIL</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="eyebrow mb-2 block">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  className="input-editorial"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="eyebrow">Password</label>
                  <Link to="/forgot-password" className="text-xs text-respondent hover:underline" data-testid="login-forgot-link">
                    Forgot?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="input-editorial"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="text-center text-sm text-ink-600 mt-6">
              No account yet?{' '}
              <Link to="/register" className="text-respondent font-medium hover:underline" data-testid="login-register-link">
                Sign up free
              </Link>
            </p>
          </div>

          <p className="text-center mt-6 font-mono text-[10px] tracking-widest text-ink-600">
            ENOTA://AUTH · SECURE [TLS 1.3]
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
